import { Component, OnInit, DestroyRef, inject } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  ClasseFuncional,
  ExaminadorUnicampConsulta,
  SituacaoFuncional,
  Titulacao,
} from 'src/app/models/examinador-unicamp-consulta'
import { ConcursosAtivos } from 'src/app/models/concursos-ativos'
import { forkJoin, take } from 'rxjs'
import { ConcursoService } from 'src/app/services/concurso.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'inscricoes-cadastrar-examinador-unicamp',
  templateUrl: './cadastrar-examinador-unicamp.component.html',
  styleUrls: ['./cadastrar-examinador-unicamp.component.scss'],
})
export class CadastrarExaminadorUnicampComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  errorMessage: string = ''
  formBuscaPessoa!: FormGroup
  formCadastroExaminador!: FormGroup
  concursosAtivos!: ConcursosAtivos[]
  concursoFixo = false
  tipoDeBusca: string = 'username'
  usuarioEncontrado: ExaminadorUnicampConsulta = {
    nomeCompleto: '',
    matricula: '',
    email: '',
    unidade: '',
    employeeType: '',
    enabled: false,
    serviceAccount: false,
    concursosProvasData: [],
  }
  usuarioValido: boolean = false
  usuarioStaff: boolean = false

  titulacaoEnum = Titulacao
  situacaoEnum = SituacaoFuncional
  classeFuncionalEnum = ClasseFuncional

  constructor(
    private readonly fb: FormBuilder,
    private readonly comissaoJulgadoraService: ComissaoJulgadoraService,
    private readonly concursoService: ConcursoService,
    private readonly unespCoreMessageService: UnespCoreMessageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.formBuscaPessoa = this.fb.group({
      tipoDeBusca: [this.tipoDeBusca, Validators.required],
      valor: ['', Validators.required],
    })

    this.formCadastroExaminador = this.fb.group({
      papel: ['', Validators.required],
      concursoId: ['', Validators.required],
    })

    forkJoin({
      params: this.route.queryParams.pipe(take(1)),
      concursos: this.comissaoJulgadoraService.buscarConcursosAtivos(),
    }).subscribe(({ params, concursos }) => {
      this.concursosAtivos = concursos

      const concursoId = params['concurso'] || this.findParentParam('id')
      if (concursoId) {
        let concurso = this.concursosAtivos.find(c => c.id === Number(concursoId))
        if (concurso) {
          this.setConcursoFixo(concurso)
        } else {
          // Concurso não está na lista de ativos — buscar pelo ID
          this.concursoService.get(Number(concursoId)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(c => {
            const entry = { id: c.id as number, titulo: c.titulo }
            this.concursosAtivos = [entry, ...this.concursosAtivos]
            this.setConcursoFixo(entry)
          })
        }
      }
    })
  }

  private findParentParam(param: string): string | null {
    let r = this.route.parent
    while (r) {
      const val = r.snapshot?.params?.[param]
      if (val) return val
      r = r.parent
    }
    return null
  }

  private setConcursoFixo(concurso: { id: number; titulo: string }): void {
    this.formCadastroExaminador.patchValue({ concursoId: concurso })
    if (this.findParentParam('id')) {
      this.concursoFixo = true
      this.formCadastroExaminador.get('concursoId')?.disable()
    }
  }

  resetSearch(): void {
    this.formBuscaPessoa.get('valor')?.reset()
    this.usuarioEncontrado = Object.assign({})
  }

  public buscarPessoa(): void {
    if (this.formBuscaPessoa.valid) {
      this.usuarioStaff = false
      this.usuarioEncontrado = Object.assign({})

      const tipo = this.formBuscaPessoa.get('tipoDeBusca')?.value
      const valor = this.formBuscaPessoa.get('valor')?.value

      this.comissaoJulgadoraService.buscarPessoa(tipo, valor).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: res => {
          this.usuarioEncontrado = res
          this.validarUsuario(this.usuarioEncontrado)
          this.formCadastroExaminador.patchValue({
            papel: '',
          })
        },
        error: err => {
          if (err.status === 404) {
            this.unespCoreMessageService.showMessageWarning('Pessoa não encontrada!')
          }
          if (err.status >= 500) {
            this.unespCoreMessageService.showMessageError('Ocorreu um erro, tente novamente mais tarde')
          }
        },
      })
    }
  }

  public cadastrarExaminador() {
    if (this.formCadastroExaminador.valid && this.usuarioValido) {
      const formCadastro = this.formCadastroExaminador.getRawValue()

      const concursoValue = formCadastro.concursoId
      const concursoId = concursoValue?.id || concursoValue

      const formCadastroComDadosUsuario = {
        ...formCadastro,
        ...this.usuarioEncontrado,
        concursoId,
      }

      this.comissaoJulgadoraService.cadastrarExaminadorInterno(formCadastroComDadosUsuario).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: resp => {
          this.unespCoreMessageService.showMessageSuccess('Examinador cadastrado com sucesso!')
        },
        error: err => {
          this.unespCoreMessageService.showMessageError(err.error.message)
        },
      })
    }
  }

  validarUsuario(usuario: ExaminadorUnicampConsulta): void {
    const classeFuncional = usuario.classeFuncional ?? ''
    const titulacao = usuario.titulacao ?? 0

    const isDocente = classeFuncional === 'D'

    this.usuarioValido = false
    let hasMinimumTitulation: boolean | null = null

    if (!usuario.serviceAccount) {
      if (isDocente) {
        hasMinimumTitulation = titulacao >= 3 && titulacao <= 7
        if (hasMinimumTitulation === true) {
          this.usuarioValido = true
          return
        }
      } else if (usuario.employeeType === 'Funcionário UNICAMP') {
        this.usuarioStaff = true
        this.usuarioValido = true
        return
      }
    }

    const erros: string[] = []

    if (usuario.serviceAccount) erros.push('ser uma conta institucional')
    if (isDocente && hasMinimumTitulation === false) erros.push('não ter a titulação mínima de Doutor')
    if (usuario.employeeType !== 'Funcionário UNICAMP') erros.push('não ser um funcionário UNICAMP')

    this.errorMessage = `Usuário não pode participar da comissão por ${erros[0]}`
  }

  navegarListaExaminadores(): void {
    const concursoValue = this.formCadastroExaminador.get('concursoId')?.value
    const concursoId = concursoValue?.id || concursoValue || this.findParentParam('id')
    if (concursoId) {
      this.router.navigate(['/concurso', concursoId, 'examinadores'])
    }
  }
}
